import { ITuple3 } from '../component-data-structure';
import { easingDataDict, IEasingTypes } from './easing';
import { TestClock } from './testClock';
import { Bezier } from './bezier';
import { Clock } from 'three';

type IAdjustFactor = 1 | -1 | [1 | -1, 1 | -1, 1 | -1];
export interface ISetTransitionInput<T> {
	from?: T;
	to?: T;
	duration?: number;
	easing?: IEasingTypes;
	delay?: number;
	// loop?: boolean;
	loopCount?: number | null;
	backAndForth?: boolean;
	resetOnFinish?: boolean;
}

export type ITransitionCallback<T> = (val: T) => any;

export type ICalcInterpolationFn<T> = (elapsedT: number) => T;
export type IInterpolationFn = (t: number) => number;
export class Transition<T extends number | ITuple3> {
	private _inputIsArray: boolean;
	private _currentValue: T;
	private _clock: TestClock | Clock;
	private _onChange?: (value: T, time: number) => any;
	private _onStart?: (value: T) => any;
	private _onFinish?: (value: T, id?: string) => any;

	private _adjFactor?: IAdjustFactor;
	private _setMetaData: () => any;
	private _valueDiff?: T;
	private _progress: number;
	private _duration: number;
	private _timeout?: ReturnType<typeof setTimeout>;
	private _stopTransition: boolean;
	private _resetTransition: boolean;
	private _forceFinish: boolean;
	private _elapsedTime: number;
	private _delay: number;
	private _loop: boolean;
	private _loopCount: number | null;
	private _initialLoopCount: number | null;
	private _backAndForth: boolean;
	private _direction: 1 | -1;
	private _resetOnFinish: boolean;
	private _active: boolean = false;

	private _startValue: T;
	private _endValue: T;
	private _easing: IEasingTypes;
	private _bezier: Bezier;
	private _isTestMode?: boolean;
	private _transitionId?: string;
	private _initialValue: T;

	constructor(d: { start: T; end: T; duration: number; easingType: IEasingTypes; delay?: number; isTestMode?: boolean; transitionId?: string, loopCount?: number | null, backAndForth?: boolean, resetOnFinish?: boolean}) {
		this._isTestMode = d.isTestMode;
		this._transitionId = d.transitionId;
		if (this._isTestMode) {
			const testClock = new TestClock(d.duration, 10, this._transitionId);
			document.addEventListener('pause-transitions', testClock.pause);
			document.addEventListener('resume-transitions', testClock.resume);
			this._clock = testClock;
		} else {
			this._clock = new Clock(false);
		}

		this._startValue = this._initialValue = d.start;
		this._endValue = d.end;
		this._easing = d.easingType;

		// console.log('within transition easing', this._easing)
		this._bezier = new Bezier(easingDataDict[this._easing])

		this._currentValue = this._startValue;
		this._duration = d.duration / 1000;
		this._delay = d.delay ?? 0;
		this._progress = 0;
		this._elapsedTime = 0;
		this._stopTransition = false;
		this._resetTransition = false;
		this._forceFinish = false;
		this._loop = d.loopCount !== 1;
		this._loopCount = typeof d.loopCount === 'undefined' ? 1 : d.loopCount;
		this._initialLoopCount = this._loopCount;
		this._backAndForth = d.backAndForth ?? false;
		this._resetOnFinish = d.resetOnFinish ?? false;
		this._inputIsArray = Array.isArray(this._startValue);
		this._direction = 1;

		this._setMetaData = () => {
			if (!this._inputIsArray) {
				this._adjFactor = this._startValue < this._endValue ? 1 : -1;
				this._valueDiff = Math.abs((this._endValue as number) - (this._startValue as number)) as T;
			} else {
				const sV = this._startValue as ITuple3;
				const eV = this._endValue as ITuple3;
				this._adjFactor = [sV[0] < eV[0] ? 1 : -1, sV[1] < eV[1] ? 1 : -1, sV[2] < eV[2] ? 1 : -1];
				this._valueDiff = [Math.abs(eV[0] - sV[0]), Math.abs(eV[1] - sV[1]), Math.abs(eV[2] - sV[2])] as T;
			}
		};
		this._setMetaData();
	}

	_interpolationFn: ICalcInterpolationFn<T> = (elapsedTime): T => {
		if (this._duration === 0) {
			this._progress = 1;
			return this.endValue;
		} else this._progress = elapsedTime / this._duration;
		//let p = easing[this._easing](this._progress);
		let p = this._bezier.evaluate(this._progress)
		p*=this._direction;
		if (!this._inputIsArray) {
			return ((this.startValue as number) + (this._valueDiff as number) * p * (this._adjFactor as 1 | -1)) as T;
		}
		const sV = this.startValue as ITuple3;
		const dV = this._valueDiff as ITuple3;
		const aF = this._adjFactor as [1 | -1, 1 | -1, 1 | -1];

		return [sV[0] + dV[0] * p * aF[0], sV[1] + dV[1] * p * aF[1], sV[2] + dV[2] * p * aF[2]] as T;
	};

	private _interpolate = () => {
		this._elapsedTime = this._clock.getElapsedTime();
		this._currentValue = this._interpolationFn(this._elapsedTime);

	
		if (this._resetTransition) {
			this._clock.stop();
			this._active = false;
			this._currentValue = this._initialValue;
			this._loopCount = this._initialLoopCount;
			this._onChange?.(this._currentValue, this._progress);
			this._onFinish?.(this._currentValue, this._transitionId);
			this._direction = 1;
			return;
		}

		if (this._stopTransition) {
			this._active = false;
			return;
		}
		
		if (this._forceFinish) {
			this._currentValue = this._endValue;
			this._active = false;
			return;
		}
		if (this._progress >= 1) {
			//this._stopTransition = true;
			this._registerFinish();
			this._active = false;
			return;
		}
		this._onChange?.(this._currentValue, this._progress);
	};

	public tick = () => {
		if (!this._active) return;
		//console.log('interpolate', this._transitionId)
		this._interpolate();
	}

	private _registerFinish() {
		this._clock.stop();
		this._currentValue = !this._resetOnFinish ? this._endValue :  this._direction === -1 ? this._endValue : this._startValue;
		this._onChange?.(this._currentValue, this._progress);
		this._onFinish?.(this._currentValue, this._transitionId);
	}

	private _start() {
		this._progress = 0;
		this._resetTransition = false;
		this._forceFinish = false;
		this._stopTransition = false;
		this._clock.start();
		if (this._isTestMode) {
			(this._clock as TestClock).finalTime = this._duration * 1000;
		}
		this._active = true;
	}

	set(input: ISetTransitionInput<T>): Transition<T> {
		clearTimeout(this._timeout as any);
		this._stopTransition = true;
		this._startValue = this._initialValue = input?.from ?? this._startValue;
		this._endValue = input?.to ?? this._endValue;
		this._easing = input?.easing ?? this._easing;
		this._bezier = new Bezier(easingDataDict[this._easing])
		this._delay = input?.delay ?? this._delay;
		this._loop = input?.loopCount !== 1;
		this._loopCount = typeof input.loopCount === 'undefined' ? 1 : input.loopCount;
		this._initialLoopCount = this._loopCount;
		this._backAndForth = input?.backAndForth ?? false;
		this._resetOnFinish = input?.resetOnFinish ?? false;
		//if (this._loopCount && this._backAndForth) this._loopCount*=2;
		if (typeof input?.duration !== 'undefined') this._duration = input.duration / 1000;
		// if (this._backAndForth) this._duration/=2;
		//if (input?.duration) this._duration = input.duration / 1000;
		this._setMetaData();
		return this;
	}

	get duration(): number {
		return this._duration;
	}

	get easing(): IEasingTypes {
		return this._easing;
	}

	get delay(): number {
		return this._delay;
	}

	get startValue(): T {
		return this._startValue;
	}

	get endValue(): T {
		return this._endValue;
	}

	get inputIsArray(): boolean {
		return this._inputIsArray;
	}

	get progress(): number {
		return this._progress;
	}

	calcValueForTime = (t: number): T => {
		return this._interpolationFn(t);
	};

	stop(): Transition<T> {
		this._stopTransition = true;
		clearTimeout(this._timeout as any);
		this._clock.stop();
		this._onChange?.(this._endValue, this._progress);
		this._onFinish?.(this._endValue, this._transitionId);
		return this;
	}

	forceFinish(): Transition<T> {
		this._forceFinish = true;
		clearTimeout(this._timeout as any);
		this._clock.stop();
		this._onChange?.(this._endValue, this._progress);
		// for (let i = 0; i < this._onFinishArray.length; i++) {
		//     this._onFinishArray[i](this._endValue);
		// }
		this._onFinish?.(this._endValue, this._transitionId);
		return this;
	}

	reset(): Transition<T> {
		this._resetTransition = true;
		this._stopTransition = true;
		this._active = false;
		this._direction = 1;
		return this;
	}

	revert(): Transition<T> {
		const end = this._endValue;
		const start = this._startValue;
		this._endValue = start;
		this._startValue = end;
		this._direction = this._direction * -1 as 1 | -1;
		return this;
	}

	start(): Transition<T> {
		//cleanup state and timers before start
		clearTimeout(this._timeout as any);
		this._progress = 0;
		this._currentValue = this._startValue;
		this._onStart?.(this._currentValue);
		this._onChange?.(this._currentValue, this._progress);
		this._resetTransition = false;
		this._stopTransition = true;
		this._forceFinish = false;

		if (typeof this._delay !== 'undefined' && !isNaN(this._delay)) {
			this._timeout = setTimeout(() => {
				this._stopTransition = false;
				this._start();
			}, this._delay);
			return this;
		}
		this._stopTransition = false;
		this._start();
		return this;
	}

	onStart(onStart?: (val: T) => any): Transition<T> {
		this._onStart = onStart ?? this._onStart;
		return this;
	}

	onChange(onChange?: (val: T, progress: number) => any): Transition<T> {
		this._onChange = onChange ?? this._onChange;
		return this;
	}

	onFinish(onFinish?: (val: T, id?: string) => any): Transition<T> {
		if (this._loop) {
			// console.log('resetart transition!!, ', this._loopCount === null, this._stopTransition)
			this._onFinish = () => {
				if (this._loopCount === 1 || (this._loopCount === null && this._stopTransition) || !this._active) {
					this._onFinish = onFinish;
					this._registerFinish();
					this._direction = 1;
					return;
				}
				if (this._backAndForth) {
					this.revert()
				}
				this.start();
				if (this._loopCount !== null) this._loopCount--
			} 
		} else {
			this._onFinish = onFinish ?? this._onFinish;
		}
		return this;
	}
}
